import { parseJwt } from '../../helpers/authUtils';
import Keycloak from 'keycloak-js';

const keycloak = new Keycloak({
    url: process.env.VUE_APP_KEYCLOAK_URL,
    realm: process.env.VUE_APP_KEYCLOAK_REALM,
    clientId: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
});

let refreshIntervalId = null;

const state = () => ({
    user: null,
    token: null,
    isAuthenticated: false,
    expiresIn: null,
});

const mutations = {
    SET_USER(state, payload) {
        state.user = payload;
        state.isAuthenticated = true;
    },
    SET_TOKEN(state, token) {
        state.token = token;
    },
    SET_EXPIRES_IN(state, seconds) {
        state.expiresIn = seconds;
    },
    LOGOUT(state) {
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        state.expiresIn = null;
    },
};

const actions = {
    restoreSession({ commit, dispatch }) {
        const token = localStorage.getItem('kc_token');
        const refreshToken = localStorage.getItem('kc_refresh_token');

        if (token && refreshToken) {
            try {
                const decoded = parseJwt(token);

                if (decoded.exp * 1000 < Date.now()) {
                    // Le token a expiré
                    dispatch('logout');
                    return;
                }

                const clientRoles = decoded.resource_access?.[decoded.azp]?.roles || [];
                const realmRoles = decoded.realm_access?.roles || [];

                commit('SET_USER', {
                    username: decoded.preferred_username,
                    email: decoded.email,
                    roles: realmRoles,
                    clientRoles,
                    raw: decoded,
                });

                commit('SET_TOKEN', token);

                // dispatch('scheduleManualTokenRefresh');
            } catch (e) {
                dispatch('logout');
            }
        }
    },
    async loginWithCredentials({ commit, dispatch }, { username, password }) {
        const response = await fetch(
            `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${process.env.VUE_APP_KEYCLOAK_REALM}/protocol/openid-connect/token`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    grant_type: 'password',
                    client_id: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
                    username,
                    password,
                }),
            }
        );

        if (!response.ok) {
            const text = await response.text();
            if (text.includes('invalid_grant')) {
                throw new Error("Identifiants incorrects ou compte verrouillé.");
            } else {
                throw new Error("Erreur d'authentification.");
            }
        }

        const data = await response.json();
        localStorage.setItem('kc_token', data.access_token);
        localStorage.setItem('kc_refresh_token', data.refresh_token);

        const decoded = parseJwt(data.access_token);
        const clientRoles = decoded.resource_access?.[decoded.azp]?.roles || [];
        const realmRoles = decoded.realm_access?.roles || [];

        commit('SET_USER', {
            username: decoded.preferred_username,
            email: decoded.email,
            roles: realmRoles,
            clientRoles,
            raw: decoded,
        });
        commit('SET_TOKEN', data.access_token);
        commit('SET_EXPIRES_IN', decoded.exp - Math.floor(Date.now() / 1000));

        // dispatch('scheduleManualTokenRefresh');
    },

    scheduleManualTokenRefresh({ commit }) {
        if (refreshIntervalId) clearInterval(refreshIntervalId);

        refreshIntervalId = setInterval(async () => {
            const refreshToken = localStorage.getItem('kc_refresh_token');
            if (!refreshToken) return;

            const response = await fetch(
                `${process.env.VUE_APP_KEYCLOAK_URL}/realms/${process.env.VUE_APP_KEYCLOAK_REALM}/protocol/openid-connect/token`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: new URLSearchParams({
                        grant_type: 'refresh_token',
                        client_id: process.env.VUE_APP_KEYCLOAK_CLIENT_ID,
                        refresh_token: refreshToken,
                    }),
                }
            );

            if (response.ok) {
                const data = await response.json();
                const decoded = parseJwt(data.access_token);
                const clientRoles = decoded.resource_access?.[decoded.azp]?.roles || [];
                const realmRoles = decoded.realm_access?.roles || [];

                commit('SET_USER', {
                    username: decoded.preferred_username,
                    email: decoded.email,
                    roles: realmRoles,
                    clientRoles,
                    raw: decoded,
                });
                commit('SET_TOKEN', data.access_token);
                commit('SET_EXPIRES_IN', decoded.exp - Math.floor(Date.now() / 1000));

                localStorage.setItem('kc_token', data.access_token);
                localStorage.setItem('kc_refresh_token', data.refresh_token);
            } else {
                commit('LOGOUT');
                localStorage.clear();
                clearInterval(refreshIntervalId);
            }
        }, 10000);
    },

    async initKeycloak({ commit, dispatch }) {
        const authenticated = await keycloak.init({
            onLoad: 'check-sso',
            checkLoginIframe: false,
        });

        if (authenticated) {
            localStorage.setItem('kc_token', keycloak.token);
            localStorage.setItem('kc_refresh_token', keycloak.refreshToken);

            const decoded = parseJwt(keycloak.token);
            const clientRoles = decoded.resource_access?.[decoded.azp]?.roles || [];
            const realmRoles = decoded.realm_access?.roles || [];

            commit('SET_USER', {
                username: decoded.preferred_username,
                email: decoded.email,
                roles: realmRoles,
                clientRoles,
                raw: decoded,
            });
            commit('SET_TOKEN', keycloak.token);
            commit('SET_EXPIRES_IN', decoded.exp - Math.floor(Date.now() / 1000));

            dispatch('scheduleKeycloakTokenRefresh');
        } else {
            await keycloak.login();
        }
    },

    scheduleKeycloakTokenRefresh({ commit }) {
        if (refreshIntervalId) clearInterval(refreshIntervalId);

        refreshIntervalId = setInterval(async () => {
            try {
                const refreshed = await keycloak.updateToken(30);
                if (refreshed) {
                    const decoded = parseJwt(keycloak.token);
                    const clientRoles = decoded.resource_access?.[decoded.azp]?.roles || [];
                    const realmRoles = decoded.realm_access?.roles || [];

                    commit('SET_USER', {
                        username: decoded.preferred_username,
                        email: decoded.email,
                        roles: realmRoles,
                        clientRoles,
                        raw: decoded,
                    });
                    commit('SET_TOKEN', keycloak.token);
                    commit('SET_EXPIRES_IN', decoded.exp - Math.floor(Date.now() / 1000));

                    localStorage.setItem('kc_token', keycloak.token);
                }
            } catch (err) {
                clearInterval(refreshIntervalId);
                await keycloak.logout({ redirectUri: window.location.origin + '/login' });
            }
        }, 10000);
    },

    logout({ commit }) {
        commit('LOGOUT');
        localStorage.clear();
        clearInterval(refreshIntervalId);
        // keycloak.logout({ redirectUri: window.location.origin + '/login' });
        window.location.href = '/';
    },
};

const getters = {
    user: state => state.user,
    token: state => state.token,
    isAuthenticated: state => state.isAuthenticated,
    expiresIn: state => state.expiresIn,
    hasRole: state => role =>
        state.user?.roles.includes(role) || state.user?.clientRoles.includes(role) || false,
};

export { keycloak };

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
